import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { useAppDispatch } from "@store";
import { openDialog } from "@store/dialogs";
import { useCallback } from "react";
import { toast } from "react-toastify";
import { IconDots } from "@tabler/icons-react";
import { handleAsyncOperation } from "@/shared";
import { useRestoreSlidesFolderMutation } from "@/shared/services";
import type { TSlidesFolder } from "@/shared/types";
import { FolderPermanentDeleteDialog } from "../dialogs/folder-permanent-delete-dialog";
import { DeleteRestoreMenu } from "./delete-restore-menu";

export const ArchivedFolderActions = ({
  isOpen,
  setIsOpen,
  folder,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  folder: TSlidesFolder;
}) => {
  const dispatch = useAppDispatch();
  const { mutateAsync: restoreFolder } = useRestoreSlidesFolderMutation();

  const handleRestore = useCallback(async () => {
    setIsOpen(false);
    await handleAsyncOperation({
      operation: async () => {
        await restoreFolder(folder._id);
        toast.success(`“${folder.title}” restored`);
      },
      onError: () => toast.error("Failed to restore folder"),
    });
  }, [restoreFolder, folder._id, folder.title, setIsOpen]);

  const handlePermanentDelete = useCallback(() => {
    setIsOpen(false);
    dispatch(
      openDialog({
        id: FolderPermanentDeleteDialog.id,
        content: FolderPermanentDeleteDialog,
        props: { folder },
      }),
    );
  }, [dispatch, folder, setIsOpen]);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Open actions for archived folder: ${folder.title}`}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          className="tw-flex tw-size-6 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-border-none tw-bg-transparent tw-p-0 tw-outline-none tw-transition-colors hover:tw-bg-blue-100"
        >
          <IconDots
            className="tw-size-3.5 tw-text-blue tw-opacity-50"
            stroke={1}
            aria-hidden="true"
          />
        </button>
      </DropdownMenuTrigger>
      <DeleteRestoreMenu onRestore={handleRestore} onDelete={handlePermanentDelete} />
    </DropdownMenu>
  );
};
